import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, RefreshCw, Send, Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryTab() {
  const token = sessionStorage.getItem('admin_token');
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'low', 'out'
  const [saving, setSaving] = useState('');
  const [alertChecking, setAlertChecking] = useState(false);
  const [alertResult, setAlertResult] = useState(null);

  const fetchInventory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/inventory`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setProducts(d.products || []);
        setLowStock(d.low_stock_count || 0);
      }
    } catch (err) { console.error('Fetch inventory failed:', err); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const updateStock = async (productId, newCount) => {
    setSaving(productId);
    try {
      await fetch(`${API_URL}/api/admin/inventory/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ inventory_count: parseInt(newCount) || 0 }),
      });
      setEditingId(null);
      fetchInventory();
    } catch (err) { console.error('Update stock failed:', err); } finally { setSaving(''); }
  };

  const updateReturnsPolicy = async (productId, policyKey) => {
    setSaving(`rp-${productId}`);
    // Optimistic local update
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, returns_policy: policyKey } : p));
    try {
      await fetch(`${API_URL}/api/admin/inventory/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ returns_policy: policyKey }),
      });
    } catch (err) { console.error('Update returns policy failed:', err); } finally { setSaving(''); }
  };

  const bulkRestock = async (amount) => {
    if (!window.confirm(`Add ${amount} units to ALL products with <10 stock?`)) return;
    setSaving('bulk');
    const lowItems = products.filter(p => (p.inventory_count || 0) < 10);
    const updates = lowItems.map(p => ({ product_id: p.id, inventory_count: (p.inventory_count || 0) + amount }));
    await fetch(`${API_URL}/api/admin/inventory/bulk/update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ updates }),
    });
    setSaving('');
    fetchInventory();
  };

  const triggerLowStockAlert = async () => {
    setAlertChecking(true);
    setAlertResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/inventory/check-low-stock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAlertResult(data);
    } catch { setAlertResult({ alerted: false, reason: 'Request failed' }); }
    finally { setAlertChecking(false); }
  };

  const filtered = products.filter(p => {
    const stock = p.inventory_count || 0;
    if (filter === 'low') return stock > 0 && stock < 10;
    if (filter === 'out') return stock <= 0;
    return true;
  });

  const getStockColor = (count) => {
    if (count <= 0) return 'text-red-500';
    if (count < 10) return 'text-amber-500';
    if (count < 50) return 'text-yellow-500';
    return 'text-emerald-500';
  };

  const getStockBar = (count) => {
    const maxDisplay = 200;
    const pct = Math.min((count / maxDisplay) * 100, 100);
    let color = 'bg-emerald-500';
    if (count <= 0) color = 'bg-red-500';
    else if (count < 10) color = 'bg-amber-500';
    else if (count < 50) color = 'bg-yellow-500';
    return { pct, color };
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading inventory...</div>;

  const totalUnits = products.reduce((s, p) => s + (p.inventory_count || 0), 0);
  const outOfStock = products.filter(p => (p.inventory_count || 0) <= 0).length;

  return (
    <div className="space-y-6" data-testid="inventory-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Products', val: products.length, color: 'text-blue-600' },
          { label: 'Total Units', val: totalUnits.toLocaleString(), color: 'text-emerald-600' },
          { label: 'Low Stock (<10)', val: lowStock, color: 'text-amber-600' },
          { label: 'Out of Stock', val: outOfStock, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Low Stock Alerts */}
      {(lowStock > 0 || outOfStock > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="low-stock-alert-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">Inventory Alert</p>
                <p className="text-xs text-amber-600">{lowStock} product(s) below 10 units &mdash; auto-alerts run every 6 hours</p>
              </div>
            </div>
            <button
              onClick={triggerLowStockAlert}
              disabled={alertChecking}
              className="px-4 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="send-low-stock-alert-btn"
            >
              {alertChecking ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              Send Alert Now
            </button>
          </div>
          {alertResult && (
            <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${alertResult.alerted ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}
              data-testid="alert-result-message">
              {alertResult.alerted
                ? `Alert sent to ${alertResult.recipient} — ${alertResult.out_of_stock || 0} out of stock, ${alertResult.critical || 0} critical, ${alertResult.warning || 0} warning`
                : `No alert sent: ${alertResult.reason || 'Unknown'}`}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {['all', 'low', 'out'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
              {f === 'all' ? `All (${products.length})` : f === 'low' ? `Low (${lowStock})` : `Out (${outOfStock})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => bulkRestock(50)} disabled={saving === 'bulk'}
            className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="bulk-restock-btn">
            {saving === 'bulk' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Bulk +50 Low Stock
          </button>
          <button onClick={() => bulkRestock(100)} disabled={saving === 'bulk'}
            className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
            Bulk +100
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase border-b bg-gray-50/50">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3 w-48">Stock Level</th>
              <th className="px-4 py-3 text-right">Units</th>
              <th className="px-4 py-3 w-44">Returns Policy</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const stock = p.inventory_count || 0;
              const bar = getStockBar(stock);
              return (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && (
                        <img src={p.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <div>
                        <p className="text-gray-800 font-medium text-xs">{p.name}</p>
                        {p.coming_soon && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Coming Soon</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{p.category || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 font-medium">${p.price?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${bar.color} transition-all`} style={{ width: `${bar.pct}%` }} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingId === p.id ? (
                      <input type="number" value={editVal} onChange={e => setEditVal(e.target.value)} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') updateStock(p.id, editVal); if (e.key === 'Escape') setEditingId(null); }}
                        className="w-16 border rounded px-2 py-1 text-xs text-right focus:border-blue-400 focus:outline-none" />
                    ) : (
                      <span className={`font-bold ${getStockColor(stock)} cursor-pointer`}
                        onClick={() => { setEditingId(p.id); setEditVal(String(stock)); }}
                        data-testid={`stock-${p.slug}`}>
                        {stock}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={p.returns_policy || ''}
                      onChange={e => updateReturnsPolicy(p.id, e.target.value)}
                      disabled={saving === `rp-${p.id}`}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[11px] bg-white focus:border-blue-400 focus:outline-none disabled:opacity-50"
                      data-testid={`returns-policy-${p.slug}`}
                      title="Returns policy override (controls Google Merchant + AI feeds)"
                    >
                      <option value="">Auto-detect</option>
                      <option value="30day_free">30-day free return</option>
                      <option value="14day_paid">14-day, customer pays</option>
                      <option value="final_sale">Final sale (no returns)</option>
                      <option value="final_sale_digital">Final sale &mdash; digital</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingId === p.id ? (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => updateStock(p.id, editVal)} disabled={saving === p.id}
                          className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold">
                          {saving === p.id ? '...' : 'Save'}
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 text-xs ml-2">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => updateStock(p.id, stock + 25)}
                          className="text-blue-500 hover:text-blue-700 text-xs font-semibold" data-testid={`restock-${p.slug}`}>+25</button>
                        <button onClick={() => updateStock(p.id, stock + 100)}
                          className="text-emerald-500 hover:text-emerald-700 text-xs font-semibold ml-2">+100</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center py-8 text-gray-400">No products match this filter.</p>}
      </div>
    </div>
  );
}
